import localStorageConfig from './config/localStorage'

const QUALTRICS_SCRIPT_URL_BASE =
	'https://zn2yzcpal564y7dkk-lavalmarketing.siteintercept.qualtrics.com/SIE/?Q_ZID=ZN_2Yzcpal564Y7dKK'
export const QUALTRICS_SCRIPT_URL =
	process.env.NODE_ENV === 'development'
		? `${QUALTRICS_SCRIPT_URL_BASE}&Q_DEBUG=1`
		: QUALTRICS_SCRIPT_URL_BASE
const QUALTRICS_CONTAINER_ID = 'ZN_2Yzcpal564Y7dKK'
let scriptLoadPromise

const QUALTRICS_API_TIMEOUT = 10000
const QUALTRICS_API_POLL_INTERVAL = 50

const getQualtricsSiteLanguage = (language) => (language === 'en' ? 'EN' : 'FR')

const getQualtricsApi = () => {
	if (typeof window === 'undefined') {
		return undefined
	}

	const api = window.QSI?.API
	return typeof api?.load === 'function' && typeof api?.run === 'function'
		? api
		: undefined
}

const ensureSiteLanguage = () => {
	let storedLanguage
	try {
		storedLanguage = window.localStorage.getItem(localStorageConfig.languageKey)
	} catch {
		storedLanguage = null
	}

	window.siteLanguage =
		storedLanguage !== null
			? getQualtricsSiteLanguage(storedLanguage)
			: window.siteLanguage === 'EN' || window.siteLanguage === 'FR'
			? window.siteLanguage
			: 'FR'
}

const ensureQualtricsContainer = () => {
	let container = document.getElementById(QUALTRICS_CONTAINER_ID)
	if (!container) {
		container = document.createElement('div')
		container.id = QUALTRICS_CONTAINER_ID
		document.body.appendChild(container)
	}

	return container
}

const loadQualtricsSurveyInternal = () => {
	if (typeof window === 'undefined' || typeof document === 'undefined') {
		return Promise.resolve()
	}

	if (!document.body) {
		return new Promise((resolve) => {
			window.addEventListener(
				'load',
				() => resolve(loadQualtricsSurveyInternal()),
				{ once: true }
			)
		})
	}

	ensureSiteLanguage()
	ensureQualtricsContainer()

	let reusableScript = document.querySelector(
		'script[data-qualtrics-site-intercept]'
	)
	if (
		reusableScript?.dataset.qualtricsLoaded === 'true' &&
		!getQualtricsApi()
	) {
		reusableScript.remove()
		reusableScript = null
	}

	if (
		getQualtricsApi() &&
		(!reusableScript || reusableScript.dataset.qualtricsLoaded === 'true')
	) {
		return Promise.resolve()
	}

	if (scriptLoadPromise) {
		return scriptLoadPromise
	}

	const script = reusableScript || document.createElement('script')

	scriptLoadPromise = new Promise((resolve, reject) => {
		let pollInterval
		let timeout
		let scriptLoaded = script.dataset.qualtricsLoaded === 'true'

		const cleanup = () => {
			clearInterval(pollInterval)
			clearTimeout(timeout)
			script.removeEventListener('error', handleScriptError)
			script.removeEventListener('load', handleScriptLoad)
		}
		const checkApiReady = () => {
			if (!scriptLoaded || !getQualtricsApi()) {
				return
			}

			cleanup()
			scriptLoadPromise = undefined
			resolve()
		}

		const handleScriptLoad = () => {
			scriptLoaded = true
			script.dataset.qualtricsLoaded = 'true'
			checkApiReady()
		}

		const handleScriptError = () => {
			cleanup()
			script.remove()
			scriptLoadPromise = undefined
			reject(new Error('Failed to load Qualtrics SIE script.'))
		}

		script.addEventListener('error', handleScriptError, { once: true })
		script.addEventListener('load', handleScriptLoad, { once: true })

		if (!reusableScript) {
			script.src = QUALTRICS_SCRIPT_URL
			script.async = true
			script.setAttribute('data-qualtrics-site-intercept', 'true')
			document.body.appendChild(script)
		}

		pollInterval = setInterval(checkApiReady, QUALTRICS_API_POLL_INTERVAL)
		timeout = setTimeout(() => {
			cleanup()
			if (!reusableScript) {
				script.remove()
			}
			scriptLoadPromise = undefined
			reject(new Error('Timed out waiting for the Qualtrics SIE API.'))
		}, QUALTRICS_API_TIMEOUT)
		if (scriptLoaded) {
			checkApiReady()
		}
	})

	// Resolves to true only for the call that injected the script.
	return scriptLoadPromise.then(() => true)
}

export const loadQualtricsSurvey = () => loadQualtricsSurveyInternal()

let languageReloadChain = Promise.resolve()

const logQualtricsFailure = (error, triggerStep, action) => {
	const failure =
		error instanceof Error
			? error
			: new Error(
					`Qualtrics ${triggerStep} failed${
						error === undefined
							? ' without a rejection reason.'
							: `: ${String(error)}`
					}`
			  )
	const failureStep = error?.qualtricsStep || triggerStep
	console.error(
		`Failed to ${action} the Qualtrics survey during ${failureStep}.`,
		failure
	)
}

// A newer language change supersedes this reload and will run its own sequence.
const isLanguageReloadStale = (siteLanguage) =>
	window.siteLanguage !== siteLanguage

const reloadQualtricsSurveyForLanguage = async (siteLanguage) => {
	let triggerStep = 'SIE initialization'

	try {
		const { qualtricsApi } = await waitForQualtricsApi()
		if (isLanguageReloadStale(siteLanguage)) {
			return
		}

		if (typeof qualtricsApi.unload === 'function') {
			triggerStep = 'API.unload()'
			await qualtricsApi.unload()
		}

		triggerStep = 'API.load()'
		await qualtricsApi.load()
		if (isLanguageReloadStale(siteLanguage)) {
			return
		}

		ensureQualtricsContainer()
		triggerStep = 'API.run()'
		await qualtricsApi.run()
	} catch (error) {
		logQualtricsFailure(error, triggerStep, 'reload')
	}
}

export const updateQualtricsSiteLanguage = (language) => {
	if (typeof window === 'undefined' || typeof document === 'undefined') {
		return Promise.resolve()
	}

	const siteLanguage = getQualtricsSiteLanguage(language)
	const languageChanged = window.siteLanguage !== siteLanguage
	window.siteLanguage = siteLanguage

	if (
		!languageChanged ||
		(!scriptLoadPromise &&
			!document.querySelector('script[data-qualtrics-site-intercept]') &&
			!getQualtricsApi())
	) {
		return Promise.resolve()
	}

	languageReloadChain = languageReloadChain.then(() =>
		reloadQualtricsSurveyForLanguage(siteLanguage)
	)
	return languageReloadChain
}

const waitForQualtricsApi = async () => {
	const injectedScript = (await loadQualtricsSurveyInternal()) === true

	const qualtricsApi = getQualtricsApi()
	if (!qualtricsApi) {
		throw new Error('Qualtrics SIE API is unavailable.')
	}

	return { qualtricsApi, injectedScript }
}

const runQualtricsSurvey = async ({
	shouldRun,
	skipRunAfterInjection = false
} = {}) => {
	let triggerStep = 'SIE initialization'

	try {
		const { qualtricsApi, injectedScript } = await waitForQualtricsApi()

		if (typeof shouldRun === 'function' && !shouldRun()) {
			return
		}

		// The SIE evaluates its intercepts by itself once loaded, so the
		// page-view trigger that injected it skips run() to avoid a double popup.
		// Form submissions always call run(): the auto-evaluation does not
		// account for the submission event.
		if (injectedScript && skipRunAfterInjection) {
			return
		}

		ensureQualtricsContainer()
		triggerStep = 'API.run()'
		await qualtricsApi.run()
	} catch (error) {
		logQualtricsFailure(error, triggerStep, 'trigger')
	}
}

export const triggerQualtricsSurvey = (options = {}) =>
	runQualtricsSurvey({ ...options, skipRunAfterInjection: true })

export const triggerQualtricsSurveyAfterFormSubmission = () => {
	return runQualtricsSurvey()
}
